import axios, { InternalAxiosRequestConfig } from 'axios';
import {
    cacheRecentAuths,
    cacheUserRole,
    cacheUserTokens,
    getCachedRecentAuths,
    getCachedUserTokens,
    removeCachedUserRole,
    removeCachedUserTokens,
    removeFailedAuthFromCachedRecentAuths,
} from 'utils/auth';
import { ICustomMap } from 'types/ICustomMap';
import { history } from 'utils/history';
import routes from 'config/routes';
import { store } from 'store/index';
import { setIsLoggedInAction, setRecentAuthsAction, setUserTokensAction } from 'store/authentication/actions';
import { adminAccess } from 'utils/adminAccess';
import queryString from 'query-string';
import parseJwt from 'utils/parseJwt';
import { getDeviceInfo } from 'utils/api/deviceInfo';
import { redactForLog } from 'utils/api/redactForLog';

const deviceInfo = getDeviceInfo();
let refreshTokenIsInProcess = false;
const baseUrl = '/api/v1/';

const logOut = (companyId?: string) => {
    removeCachedUserTokens();
    removeCachedUserRole();
    if (companyId) {
        removeFailedAuthFromCachedRecentAuths(companyId);
    }
    store.dispatch(setIsLoggedInAction(false));
    store.dispatch(setUserTokensAction(null));
    store.dispatch(setRecentAuthsAction(getCachedRecentAuths()));
    adminAccess.deleteToken();
    history.push(routes.auth.enterWorkspace);
    refreshTokenIsInProcess = false;
};

const api = axios.create({
    baseURL: baseUrl,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: {
        serialize: params => {
            return queryString.stringify(params, { skipEmptyString: true, skipNull: true });
        },
    },
});

api.interceptors.request.use(
    // @ts-ignore
    async (configuration: InternalAxiosRequestConfig & { isExternalCall: boolean; isCritical: boolean; useCustomAuthHeaders: boolean }) => {
        const { isExternalCall, useCustomAuthHeaders, isCritical, ...config } = configuration;
        const cachedTokens = getCachedUserTokens();
        const adminAccessToken = adminAccess.getToken(); // admin access token for client accounts

        // stop request if it is blocked, and it is not critical(refresh token request)
        if (refreshTokenIsInProcess && !isCritical) {
            // window.console.log(
            //     'Blocking Request. ',
            //     'refreshTokenIsInProccess = ',
            //     refreshTokenIsInProcess,
            //     'isCritical = ',
            //     isCritical,
            //     new Date().toUTCString(),
            // );
            return Promise.reject(new Error('Request blocked'));
        }

        const deviceIdHeaders = {
            'SL-DEVICE-UID': deviceInfo.id,
            'SL-DEVICE-NAME': deviceInfo.name,
        };

        if (cachedTokens?.access_token && !isExternalCall) {
            // this check is needed if need to path token direct throught api call and ignore cached tokens
            // const hasPassedAuthorization = !!config?.headers?.Authorization || !!config?.headers?.authorization;
            const auth = useCustomAuthHeaders ? {} : { Authorization: `Bearer ${adminAccessToken || cachedTokens?.access_token}` };
            // @ts-ignore
            config.headers = {
                ...config.headers,
                ...auth,
            };
        }

        return {
            ...config,
            headers: {
                ...config.headers,
                ...(configuration.baseURL === baseUrl ? deviceIdHeaders : {}), // only add device headers to internal api calls
            },
        };
    },
);

api.interceptors.response.use(
    response => response.data,
    async error => {
        window.console.error('API error:', redactForLog(error?.response), error?.response?.status);
        if (error?.response && error?.response?.status === 401 && !refreshTokenIsInProcess) {
            refreshTokenIsInProcess = true;

            if (adminAccess.getToken()) {
                adminAccess.deleteToken();
                window?.location.reload();
            }

            const cachedTokens = getCachedUserTokens();

            if (!cachedTokens?.refresh_token) {
                // window.console.error('401 - No local refresh token found at ', new Date().toUTCString());
                // window.console.log('Cached Tokens - ', JSON.stringify(cachedTokens));
                const { company_id } = parseJwt(error?.config?.headers?.Authorization?.replace('Bearer ', '')) || {};
                logOut(company_id);
                refreshTokenIsInProcess = false;
                return Promise.reject(error);
            }

            const formData = new FormData();
            formData.append('grant_type', 'refresh_token');
            formData.append('refresh_token', cachedTokens?.refresh_token || '');

            // const getAccessTokenStartDate = new Date().toUTCString();
            // window.console.log(`----------GET ACCESS TOKEN START ${getAccessTokenStartDate}----------`);
            // window.console.log('sending token', cachedTokens);

            try {
                // window.console.log('starting request at', new Date().toUTCString());
                const { data: user }: ICustomMap = await axios.post('/api/v1/get_access_token/', formData, {
                    // @ts-ignore
                    isCritical: true,
                    headers: {
                        'Content-Type': 'x-www-form-urlencoded',
                        'SL-DEVICE-UID': deviceInfo.id,
                        'SL-DEVICE-NAME': deviceInfo.name,
                    },
                });

                // window.console.log('successfully end request at = ', new Date().toUTCString());
                // window.console.log('received tokens', JSON.stringify(user));

                const { company_id: companyId } =
                    parseJwt(user.access_token) || parseJwt(error?.config?.headers?.Authorization?.replace('Bearer ', '')) || {};

                if (!user?.access_token) {
                    window.console.error('get_access_token has not returned access token');
                    refreshTokenIsInProcess = false;
                    // const { company_id } = parseJwt(error?.config?.headers?.Authorization?.replace('Bearer ', '')) || {};
                    logOut(companyId);
                }

                if (!user?.refresh_token) {
                    window.console.error('get_access_token has not returned refresh token');
                    refreshTokenIsInProcess = false;
                    // const { company_id } = parseJwt(error?.config?.headers?.Authorization?.replace('Bearer ', '')) || {};
                    logOut(companyId);
                }

                const recentAuths = getCachedRecentAuths() || {};

                // Update cached recent AUTH
                if (companyId && recentAuths) {
                    recentAuths[companyId] = {
                        refresh_token: user?.refresh_token,
                        access_token: user?.access_token,
                        id_str: companyId,
                        userId: recentAuths[companyId]?.userId || '',
                        commonUserId: recentAuths[companyId]?.commonUserId || '',
                        shortName: recentAuths[companyId]?.shortName || '',
                        workspaceName: recentAuths[companyId]?.workspaceName || '',
                        identityProvider: recentAuths[companyId]?.identityProvider || '',
                    };
                    cacheRecentAuths(recentAuths);
                }

                // Update cached tokens
                cacheUserTokens({
                    access_token: user.access_token,
                    refresh_token: user.refresh_token,
                });

                if (user.role) {
                    cacheUserRole(user.role);
                }

                refreshTokenIsInProcess = false;
                // window.console.log('final end date', new Date().toUTCString());
                // window.console.log(`----------END GET ACCESS TOKEN (success) ${getAccessTokenStartDate}----------`);
                window.location.reload();

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                window.console.error('Get fresh access token error ', err);
                refreshTokenIsInProcess = false;
                const canLogOut = err?.response?.status >= 400 && err?.response?.status < 500;

                if (canLogOut) {
                    const { company_id } = parseJwt(error?.config?.headers?.Authorization?.replace('Bearer ', '')) || {};
                    // remove user from local storage to log user out
                    logOut(company_id);
                }
                // window.console.log('final end date', new Date().toUTCString());
                // window.console.log(`----------END GET ACCESS TOKEN (error) ${getAccessTokenStartDate}----------`);
            }
        }
        return Promise.reject(error);
    },
);

export default api;
